import { Simulation } from '@/hooks/useSimulation';
import { FiatAccount } from '@/types/contacts';
import { CurrencyCodeType, DashboardOperation, PaymentRailType } from '@/types/currency';
import { TransactionResponse } from '@/types/transaction';
import { IOFAmount } from '@/utility/IOFAmount';
import { GenericAction } from '@/utility/reducers';

export interface WithdrawalFlowStateBase {
  readonly paymentRail: PaymentRailType;
  readonly fundingMethod: PaymentRailType;
  readonly fromAmount: number | null /* null means: unset */;
  readonly fromCurrency: CurrencyCodeType;
  readonly toAmount: number | null;
  readonly toCurrency: CurrencyCodeType;
  readonly simulationId: string | null;
  readonly transactionId: string | null;
  readonly destination: string;
}

export interface PIXWithdrawalFlowState extends WithdrawalFlowStateBase {
  readonly IOF?: {
    readonly label: string;
    readonly amount: IOFAmount;
  };
}

export interface SPEIWithdrawalFlowState extends WithdrawalFlowStateBase {
  readonly recipientName: string;
  readonly clabe: string;
  readonly accountNumber: string;
  readonly institution: string;
}

export interface ACHOrWireWithdrawalFlowState extends WithdrawalFlowStateBase {
  readonly beneficiaryName: string;
  readonly accountType: 'checking' | 'savings';
  readonly bankName: string;
  readonly bankAddress: string;
  readonly accountNumber: string;
  readonly routingNumber: string;
}

export interface CryptoWithdrawalFlowState extends WithdrawalFlowStateBase {
  readonly chain: string;
  readonly address: string;
}

export type WithdrawalFlowState =
  | PIXWithdrawalFlowState
  | SPEIWithdrawalFlowState
  | ACHOrWireWithdrawalFlowState
  | CryptoWithdrawalFlowState;

export const initialState: WithdrawalFlowState = {
  fundingMethod: PaymentRailType.unset,
  fromAmount: null,
  fromCurrency: CurrencyCodeType.UNSET,
  toAmount: null,
  paymentRail: PaymentRailType.unset,
  toCurrency: CurrencyCodeType.UNSET,
  simulationId: null,
  transactionId: null,
  destination: '',
};

export enum WithdrawalFlowActionType {
  transactionFlowChanged = 'transactionFlowChanged',
  fromAmountChanged = 'fromAmountChanged',
  fromCurrencyChanged = 'fromCurrencyChanged',
  simulationCreated = 'simulationCreated',
  withdrawalFlowFinished = 'withdrawalFlowFinished',
  transactionCreated = 'transactionCreated',
  destinationChanged = 'destinationChanged',
}

export type TransactionFlowChangedAction = GenericAction<
  WithdrawalFlowActionType.transactionFlowChanged,
  DashboardOperation
>;

export type DestinationChangedAction = GenericAction<
  WithdrawalFlowActionType.destinationChanged,
  FiatAccount
>;

export type FromAmountChangedAction = GenericAction<
  WithdrawalFlowActionType.fromAmountChanged,
  number | null
>;

export type FromCurrencyChangedAction = GenericAction<
  WithdrawalFlowActionType.fromCurrencyChanged,
  CurrencyCodeType
>;

export type SimulationCreatedAction = GenericAction<
  WithdrawalFlowActionType.simulationCreated,
  Simulation
>;

export type ResetWithdrawalFlowAction = GenericAction<
  WithdrawalFlowActionType.withdrawalFlowFinished,
  void
>;

export type TransactionCreatedAction = GenericAction<
  WithdrawalFlowActionType.transactionCreated,
  TransactionResponse
>;

export type WithdrawalFlowAction =
  | TransactionFlowChangedAction
  | FromAmountChangedAction
  | SimulationCreatedAction
  | ResetWithdrawalFlowAction
  | TransactionCreatedAction
  | DestinationChangedAction
  | FromCurrencyChangedAction;

export const withdrawalFlowReducer = (
  state: WithdrawalFlowState,
  action: WithdrawalFlowAction,
): WithdrawalFlowState => {
  switch (action.type) {
    case WithdrawalFlowActionType.transactionFlowChanged: {
      const { currency, destinations } = action.payload;

      return {
        ...state,
        fromAmount: null,
        fromCurrency: currency,
        toCurrency: destinations[0].currency ?? CurrencyCodeType.UNSET,
        fundingMethod:
          destinations.length === 0
            ? PaymentRailType.unset
            : destinations[0].fundingMethods?.length
              ? destinations[0].fundingMethods[0]
              : PaymentRailType.unset,
      };
    }
    case WithdrawalFlowActionType.fromAmountChanged: {
      const amount = action.payload;
      return {
        ...state,
        toAmount: null,
        simulationId: null,
        transactionId: null,
        fromAmount: amount,
      };
    }
    case WithdrawalFlowActionType.simulationCreated: {
      const { id, to } = action.payload;
      return {
        ...state,
        simulationId: id,
        toAmount: to.value,
      };
    }
    case WithdrawalFlowActionType.withdrawalFlowFinished: {
      return {
        ...state,
        fundingMethod: PaymentRailType.unset,
        fromAmount: null,
        fromCurrency: CurrencyCodeType.UNSET,
        toAmount: null,
        toCurrency: CurrencyCodeType.UNSET,
        simulationId: null,
        transactionId: null,
      };
    }
    case WithdrawalFlowActionType.fromCurrencyChanged:
      return {
        ...state,
        fromCurrency: action.payload,
      };
    case WithdrawalFlowActionType.transactionCreated: {
      const { id } = action.payload;
      return {
        ...state,
        transactionId: id,
      };
    }
    case WithdrawalFlowActionType.destinationChanged: {
      return {
        ...state,
        paymentRail: action.payload.fiatAccountType as unknown as PaymentRailType,
        destination: action.payload.id,
      };
    }
    default:
      return state;
  }
};

export const transactionFlowChanged = (
  payload: TransactionFlowChangedAction['payload'],
): TransactionFlowChangedAction => ({
  type: WithdrawalFlowActionType.transactionFlowChanged,
  payload: payload,
});

export const fromAmountChanged = (
  payload: FromAmountChangedAction['payload'],
): FromAmountChangedAction => ({
  type: WithdrawalFlowActionType.fromAmountChanged,
  payload: payload,
});

export const fromCurrencyChanged = (
  payload: FromCurrencyChangedAction['payload'],
): FromCurrencyChangedAction => ({
  type: WithdrawalFlowActionType.fromCurrencyChanged,
  payload: payload,
});

export const destinationChanged = (payload: FiatAccount): DestinationChangedAction => ({
  type: WithdrawalFlowActionType.destinationChanged,
  payload: payload,
});
